import type { Roles } from '@/types/roles.types';
import { apiService } from '@/services/core/api.service';

export interface BackendPermission {
  id: string;
  key: string;
  name: string;
  description?: string | null;
  shortDescription?: string | null;
  longDescription?: string | null;
  moduleId: string;
  createdAt: string;
  updatedAt: string;
}

export interface BackendResource {
  name: string;
  permissions: BackendPermission[];
}

export interface BackendModule {
  id: string;
  name: string;
  key: string;
  description?: string;
  resources: BackendResource[];
}

export interface CreateRoleDto {
  name: string;
  description?: string;
  isActive?: boolean;
  permissionKeys?: string[];
}

export interface UpdateRoleDto {
  name?: string;
  description?: string;
  isActive?: boolean;
  permissionKeys?: string[];
}

export interface PaginatedRolesResponse {
  data: Roles[];
  meta: {
    total: number;
    page: number;
    limit: number;
    totalPages: number;
  };
}

const withAuth = (token: string): HeadersInit => ({
  Authorization: `Bearer ${token}`,
});

export const rolesApi = {
  /**
   * Crear rol
   * POST /roles
   */
  create(data: CreateRoleDto, token: string): Promise<Roles> {
    return apiService.request<Roles>("/roles", {
      method: "POST",
      headers: withAuth(token),
      body: JSON.stringify(data),
    });
  },

  /**
   * Listado de roles (paginado)
   * GET /roles?page=1&limit=10
   */
  getAll(
    token: string,
    params?: { page?: number; limit?: number }
  ): Promise<PaginatedRolesResponse> {
    const query = new URLSearchParams();

    if (params?.page) query.append("page", String(params.page));
    if (params?.limit) query.append("limit", String(params.limit));

    const url = query.toString() ? `/roles?${query}` : "/roles";

    return apiService.request<PaginatedRolesResponse>(url, {
      headers: withAuth(token),
    });
  },

  /**
   * Obtener rol por ID
   * GET /roles/:id
   */
  getById(roleId: string, token: string): Promise<Roles> {
    return apiService.request<Roles>(`/roles/${roleId}`, {
      headers: withAuth(token),
    });
  },

  /**
   * Editar rol
   * PATCH /roles/:id
   */
  update(roleId: string, data: UpdateRoleDto, token: string): Promise<Roles> {
    return apiService.request<Roles>(`/roles/${roleId}`, {
      method: "PATCH",
      headers: withAuth(token),
      body: JSON.stringify(data),
    });
  },

  /**
   * Eliminar rol (borrado lógico: la fila y su historial se conservan)
   * DELETE /roles/:id
   *
   * Responde 409 si algún usuario todavía lo tiene, o si es un rol del sistema,
   * y el mensaje ya viene listo para mostrarse.
   */
  remove(roleId: string, token: string): Promise<void> {
    return apiService.request<void>(`/roles/${roleId}`, {
      method: "DELETE",
      headers: withAuth(token),
    });
  },

  /**
   * Obtener catálogo de permisos (árbol completo)
   * GET /permissions
   */
  getPermissions(token: string): Promise<BackendModule[]> {
    return apiService.request<BackendModule[]>("/permissions", {
      headers: withAuth(token),
    });
  },
};
